import { Injectable } from '@angular/core';
import { ActivityConfirmationsDaoService } from '../activity-confirmations-dao.service';
import { ActivityConfirmation } from 'src/app/_model/activity-confirmation';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { ActivityConfirmationsDbTableDefinition } from './activity-confirmations-db-table-definition';

// Keep in sync with toParameters() - 28 columns, 28 placeholders.
// NOTE: the table also has `acstatus`, `acislocked`, `draftdynformid`,
// `isconflictbranch`, `createdcellphoneid` and `createdcellphonelabel` columns from
// earlier iterations. They are deliberately never read or written - an AC DTO has
// `status`/`islocked`, a single `dynformid`, `isconflictversion` and
// `createddeviceid`/`createddevicelabel`.
const AC_COLUMNS =
  '(id, acukey, label, status, islocked, isconflictversion, reftstamp,' +
  ' eventtstamp, ownerid, ownerobjref, reportfileid, origfileid, dynformid,' +
  ' filledformdata, numberrangevalue, draftownerempid,' +
  ' draftownermcid, draftownerservicejobid, jobsectionid, entitycreatedtstamp,' +
  ' createuser, createdempid, createddeviceid, createddevicelabel,' +
  ' previousversionactconfid, nextversionactconfid, chainrootacid, databag)';
// Grouped in nines so the count stays verifiable by eye.
const AC_PLACEHOLDERS =
  ' VALUES (?,?,?,?,?,?,?,?,?,' + // id .. ownerid
  '?,?,?,?,?,?,?,?,?,' + // ownerobjref .. draftownerservicejobid
  '?,?,?,?,?,?,?,?,?,?)'; // jobsectionid .. databag

@Injectable()
export class ActivityConfirmationsDbDaoService implements ActivityConfirmationsDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private activityConfirmationsTableDefinition: ActivityConfirmationsDbTableDefinition,
  ) {}

  public async saveActivityConfirmations(entries: ActivityConfirmation[]): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      // INSERT OR REPLACE on the acid: the sync and a dynform upload response can
      // both deliver the same AC, and the `updateactivityconfirmation` action may
      // fire more than once for the same upload - neither must create duplicates.
      const queries: Query[] = entries.map((entry) => ({
        statement:
          'INSERT OR REPLACE INTO ' +
          this.activityConfirmationsTableDefinition.tableName +
          AC_COLUMNS +
          AC_PLACEHOLDERS,
        parameters: this.toParameters(entry),
      }));
      await this.dbPersistenceService.executeBatch(db, queries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error saving activity confirmations to db',
        error?.message,
      );
    }
  }

  public async getActivityConfirmations(): Promise<ActivityConfirmation[]> {
    const db = await this.dbPersistenceService.getImsDb();
    const entries: ActivityConfirmation[] = [];

    try {
      const data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.activityConfirmationsTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        entries.push(this.fromRow(data.values[i]));
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading activity confirmations from db',
        error?.message,
      );
    }

    return entries;
  }

  public async updateFilledFormData(acId: number, filledFormData: string): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'UPDATE ' +
          this.activityConfirmationsTableDefinition.tableName +
          ' SET filledformdata = ? WHERE id = ?;',
        parameters: [filledFormData, acId],
      });
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error updating filledformdata of activity confirmation',
        acId,
        error?.message,
      );
    }
  }

  public async updateDraftOwner(
    acId: number,
    draftOwnerEmpId: number,
    draftOwnerMcId: number,
  ): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'UPDATE ' +
          this.activityConfirmationsTableDefinition.tableName +
          ' SET draftownerempid = ?, draftownermcid = ?, draftownerservicejobid = NULL' +
          ' WHERE id = ?;',
        parameters: [draftOwnerEmpId, draftOwnerMcId, acId],
      });
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error updating draft owner of activity confirmation',
        acId,
        error?.message,
      );
    }
  }

  public async reset(): Promise<void> {
    // No local-only ACs exist
  }

  public async clear(): Promise<void> {
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.activityConfirmationsTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.activityConfirmationsTableDefinition.tableName,
    );
  }

  // Private Functions
  private toParameters(entry: ActivityConfirmation): any[] {
    return [
      Number(entry.getImsId()),
      entry.acukey,
      entry.label,
      entry.acStatus,
      entry.acIsLocked ? 1 : 0,
      entry.isConflictVersion ? 1 : 0,
      entry.refTstamp,
      entry.eventTstamp,
      entry.ownerId,
      entry.ownerObjRef,
      entry.reportFileId,
      entry.origFileId,
      entry.dynFormId,
      entry.filledFormData,
      entry.numberRangeValue,
      entry.draftOwnerEmpId,
      entry.draftOwnerMcId,
      entry.draftOwnerServiceJobId,
      entry.jobSectionId,
      entry.entityCreatedTstamp,
      entry.createUser,
      entry.createdEmpId,
      entry.createdDeviceId,
      entry.createdDeviceLabel,
      entry.previousVersionActConfId,
      entry.nextVersionActConfId,
      entry.chainRootAcId,
      entry.databag ? JSON.stringify(entry.databag) : undefined,
    ];
  }

  private fromRow(row: any): ActivityConfirmation {
    return ActivityConfirmation.fromDto(
      {
        id: row['id'],
        acukey: row['acukey'],
        label: row['label'],
        status: row['status'],
        // Stored as 0/1 by SQLite
        islocked: !!row['islocked'],
        isconflictversion: !!row['isconflictversion'],
        reftstamp: row['reftstamp'],
        eventtstamp: row['eventtstamp'],
        ownerid: row['ownerid'],
        ownerobjref: row['ownerobjref'],
        reportfileid: row['reportfileid'],
        origfileid: row['origfileid'],
        dynformid: row['dynformid'],
        filledformdata: row['filledformdata'],
        numberrangevalue: row['numberrangevalue'],
        draftownerempid: row['draftownerempid'],
        draftownermcid: row['draftownermcid'],
        draftownerservicejobid: row['draftownerservicejobid'],
        jobsectionid: row['jobsectionid'],
        entitycreatedtstamp: row['entitycreatedtstamp'],
        createuser: row['createuser'],
        createdempid: row['createdempid'],
        createddeviceid: row['createddeviceid'],
        createddevicelabel: row['createddevicelabel'],
        previousversionactconfid: row['previousversionactconfid'],
        nextversionactconfid: row['nextversionactconfid'],
        chainrootacid: row['chainrootacid'],
      },
      row['databag'] ? JSON.parse(row['databag']) : undefined,
    );
  }
}
